/* ============================================================
   Monsuri Journal — article runtime
   Loaded only on the Monsuri Journal article template.
   Vanilla ES6, no dependencies. Defer-loaded.

   Behaviors (Phase B):
   1. Progress bar — fills as the user scrolls the article body.
   2. TOC active section — IntersectionObserver tracks visible h2[data-num].
      Updates every TOC instance present (rail + mobile inline).
   3. TOC collapse toggle (per instance).
   4. Mobile sticky CTA — appears at 38% scroll, hides under 25% (hysteresis).
   5. Quiz state machine — branching by first answer, ends with product card.
   6. Smooth-scroll to TOC anchors with offset for sticky header.

   No use of innerHTML — all dynamic content built via createElement +
   textContent. Quiz config is JSON-encoded server-side from a metafield
   (Liquid `| json`) and parsed on the client.
   ============================================================ */

(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function el(tag, attrs, text) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'className') node.className = attrs[k];
        else node.setAttribute(k, attrs[k]);
      });
    }
    if (text != null) node.textContent = String(text);
    return node;
  }

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  ready(function () {
    var article = $('.m-article');
    if (!article) return;

    initProgressBar(article);
    initTOC(article);
    initMobileCTA(article);
    initQuiz(article);
    // Phase B: dropped initStickyHeader (the .art-hdr markup was removed).
  });

  // ============================================================
  // Progress bar
  // ============================================================
  function initProgressBar(article) {
    var fill = $('.blog-progress-fill', article);
    var body = $('.art-body', article);
    if (!fill || !body) return;

    var raf = null;
    function update() {
      raf = null;
      var rect = body.getBoundingClientRect();
      var bodyTop = rect.top + window.scrollY;
      var bodyHeight = body.offsetHeight;
      var viewportH = window.innerHeight;
      var scrolled = window.scrollY - bodyTop + viewportH * 0.4;
      var pct = bodyHeight > 0 ? Math.max(0, Math.min(1, scrolled / bodyHeight)) : 0;
      fill.style.width = (pct * 100).toFixed(2) + '%';
    }
    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  // ============================================================
  // Sticky header observer DROPPED in Phase B — `.art-hdr` markup
  // was removed from main-article-monsuri.liquid (the duplicate
  // mini-header was reading as redundant chrome above the H1).
  // ============================================================
  // ============================================================
  // TOC — populate from h2[data-num], track active, collapse toggle
  // ============================================================
  function initTOC(article) {
    var tocs = $$('.art-toc', article);
    if (!tocs.length) return;

    var headings = $$('.art-body h2[data-num]', article);
    if (!headings.length) return;

    // Ensure each heading has an id (used by anchor + IntersectionObserver).
    headings.forEach(function (h, i) {
      if (!h.id) h.id = 'm-section-' + (i + 1);
    });

    // Phase B: there may be multiple TOC instances (rail + inline). Populate
    // each, wire up click + collapse handlers per instance.
    tocs.forEach(function (toc) {
      var list = toc.querySelector('.art-toc-list');
      var head = toc.querySelector('.art-toc-head');
      if (!list) return;

      if (!list.children.length) {
        headings.forEach(function (h, i) {
          var a = el('a', { href: '#' + h.id, 'data-target': h.id });
          a.appendChild(el('span', { className: 'num' }, h.getAttribute('data-num') || String(i + 1).padStart(2, '0')));
          a.appendChild(el('span', { className: 'label' }, (h.getAttribute('data-label') || h.textContent).trim()));
          var li = el('li');
          li.appendChild(a);
          list.appendChild(li);
        });
      }

      list.addEventListener('click', function (e) {
        var a = e.target.closest('a[href^="#"]');
        if (!a) return;
        var id = a.getAttribute('href').slice(1);
        var target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        var top = target.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: top, behavior: 'smooth' });
        history.replaceState(null, '', '#' + id);
      });

      if (head) {
        head.addEventListener('click', function () {
          toc.classList.toggle('collapsed');
          head.setAttribute('aria-expanded', toc.classList.contains('collapsed') ? 'false' : 'true');
        });
      }
    });

    if (!('IntersectionObserver' in window)) return;

    // Collect anchors from BOTH possible TOC placements (rail + inline).
    // Phase B may render the TOC twice (e.g. toc_position=both) — both
    // copies should track the same active section.
    var anchors = {};
    $$('a[data-target]', article).forEach(function (a) {
      var key = a.getAttribute('data-target');
      if (!anchors[key]) anchors[key] = [];
      anchors[key].push(a);
    });

    var observer = new IntersectionObserver(function () {
      var bestId = null, bestY = Infinity;
      headings.forEach(function (h) {
        var rect = h.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.4 && rect.top > -rect.height * 2) {
          if (rect.top < bestY) { bestY = rect.top; bestId = h.id; }
        }
      });
      Object.keys(anchors).forEach(function (id) {
        var isActive = id === bestId;
        anchors[id].forEach(function (a) {
          a.classList.toggle('active', isActive);
        });
      });
      // Phase B: dropped the rail-progress section-progress updater.
      // The rail no longer has a "You are here" block; the active TOC
      // anchor in the rail-mode TOC carries the same information.
    }, {
      threshold: [0, 0.25, 0.5, 0.75, 1],
      rootMargin: '-80px 0px -50% 0px'
    });

    headings.forEach(function (h) { observer.observe(h); });
  }

  // ============================================================
  // Mobile sticky CTA — show at 38% scroll, hide below 25%
  // ============================================================
  function initMobileCTA(article) {
    var cta = $('.mobile-cta', article);
    if (!cta) return;
    var body = $('.art-body', article);
    if (!body) return;

    var shown = false;
    var raf = null;

    function update() {
      raf = null;
      var rect = body.getBoundingClientRect();
      var bodyTop = rect.top + window.scrollY;
      var bodyHeight = body.offsetHeight;
      var viewportH = window.innerHeight;
      var scrolled = window.scrollY - bodyTop + viewportH * 0.5;
      var pct = bodyHeight > 0 ? scrolled / bodyHeight : 0;
      if (!shown && pct > 0.38) {
        shown = true;
        cta.setAttribute('data-show', 'true');
      } else if (shown && pct < 0.25) {
        shown = false;
        cta.setAttribute('data-show', 'false');
      }
    }
    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  // ============================================================
  // Quiz — DOM-built, no innerHTML
  // ============================================================
  function initQuiz(article) {
    var quiz = $('.quiz[data-quiz="1"]', article);
    if (!quiz) return;

    var configEl = $('script[data-quiz-config]', quiz);
    if (!configEl) return;

    var config;
    try { config = JSON.parse(configEl.textContent); }
    catch (e) { return; }
    if (!config || !Array.isArray(config.questions) || !config.results) return;

    var stepEl = $('.quiz-step', quiz);
    if (!stepEl) return;

    var state = { step: 0, answers: [] };

    function clear(node) { while (node.firstChild) node.removeChild(node.firstChild); }

    function renderStep() {
      clear(stepEl);
      var q = config.questions[state.step];
      if (!q) return renderResult();

      var card = el('div', { className: 'quiz-card' });

      var prog = el('div', { className: 'quiz-prog' });
      config.questions.forEach(function (_, i) {
        prog.appendChild(el('span', i <= state.step ? { className: 'on' } : null));
      });
      card.appendChild(prog);

      card.appendChild(el('h3', { className: 'quiz-q' }, q.q));

      var opts = el('div', { className: 'quiz-opts' });
      (q.options || []).forEach(function (opt) {
        var btn = el('button', { type: 'button', className: 'quiz-opt', 'data-key': opt.key });
        btn.appendChild(el('span', { className: 'quiz-opt-l' }, opt.label));
        if (opt.sub) btn.appendChild(el('span', { className: 'quiz-opt-s' }, opt.sub));
        opts.appendChild(btn);
      });
      card.appendChild(opts);

      stepEl.appendChild(card);
    }

    function renderResult() {
      clear(stepEl);
      var key = state.answers[0] || 'default';
      var r = config.results[key] || config.results['default'];
      if (!r) return;

      var wrap = el('div', { className: 'quiz-result' });
      wrap.appendChild(el('div', { className: 'quiz-result-eb' }, '— ' + (r.result_eyebrow || 'Your ritual') + ' —'));

      var card = el('div', { className: 'quiz-result-card' });

      var imgWrap = el('div', { className: 'quiz-result-img' });
      if (r.image) {
        var img = el('img', { src: r.image, alt: r.name || '', loading: 'lazy' });
        imgWrap.appendChild(img);
      }
      card.appendChild(imgWrap);

      var body = el('div', { className: 'quiz-result-body' });
      if (r.eyebrow) body.appendChild(el('div', { className: 'quiz-result-mini' }, r.eyebrow));
      body.appendChild(el('h4', null, r.name || ''));
      if (r.why) body.appendChild(el('p', null, r.why));

      var meta = el('div', { className: 'quiz-result-meta' });
      meta.appendChild(el('span', { className: 'quiz-result-price' }, r.price || ''));
      if (r.rating) {
        var ratingSpan = el('span', { className: 'quiz-result-stars' });
        ratingSpan.appendChild(el('span', { className: 'stars' }, '★★★★★'));
        ratingSpan.appendChild(document.createTextNode(' ' + r.rating));
        meta.appendChild(ratingSpan);
      }
      body.appendChild(meta);

      var ctaUrl = r.url || (r.handle ? '/products/' + r.handle : '#');
      var cta = el('a', { className: 'btn btn-primary btn-block', href: ctaUrl }, r.cta_label || 'View product');
      cta.appendChild(document.createTextNode(' '));
      cta.appendChild(el('span', { className: 'arr' }, '→'));
      body.appendChild(cta);

      body.appendChild(el('button', { type: 'button', className: 'quiz-retake' }, 'Retake the quiz'));

      card.appendChild(body);
      wrap.appendChild(card);
      stepEl.appendChild(wrap);
    }

    stepEl.addEventListener('click', function (e) {
      var opt = e.target.closest('.quiz-opt');
      if (opt) {
        state.answers.push(opt.getAttribute('data-key'));
        if (state.step + 1 >= config.questions.length) renderResult();
        else { state.step += 1; renderStep(); }
        return;
      }
      var retake = e.target.closest('.quiz-retake');
      if (retake) {
        state = { step: 0, answers: [] };
        renderStep();
      }
    });

    renderStep();
  }
})();

/* ============================================================
   Klaviyo + Shopify-form swap controller (Phase B.1, 2026-05-10)
   ────────────────────────────────────────────────────────────
   Pairs with `.m-klaviyo-fallback` markup in `snippets/article-rail.liquid`
   and `sections/article-newsletter.liquid`, and the CSS class-gate
   `.m-klaviyo-ready + .m-klaviyo-fallback { display: none; }` in
   `assets/article-monsuri.css`.

   Why this exists (adversarial review by0r4ter6 / bl20yzhgi):
   - The earlier `:not(:empty)` CSS predicate hid the Shopify fallback
     on ANY child node injected into the Klaviyo mount, including
     loaders / wrappers / error containers / partial markup. If
     Klaviyo stalled after injecting non-functional markup, the user
     was left with neither form working.
   - Display:none-swapping a focused fallback mid-typing dropped the
     user's focus and in-progress email value silently — bad for both
     sighted and screen-reader users.

   This controller fixes both:

   1. USABILITY GATE — only sets `m-klaviyo-ready` (which triggers the
      CSS hide) once the mount contains BOTH `input[type="email"]`
      AND a submit-capable control, both of which must be VISIBLE
      (non-zero bounding rect), ENABLED (no `disabled`), NOT inside an
      aria-hidden subtree, and SHARE A COHERENT SUBMISSION SCOPE (same
      <form> ancestor or no form at all). The stricter gate (added
      2026-05-10 per adversarial review bopqt638n) rejects loader
      markup, disabled placeholders, hidden controls, and email/submit
      pairs that aren't part of the same submission handler. Stalls or
      partial injection do not hide the fallback. Validates on every
      mutation — both childList (Klaviyo inserting/removing nodes) AND
      attribute toggles on `class` / `style` / `aria-hidden` / `disabled`
      / `hidden` (Klaviyo flipping a pre-inserted shell from hidden to
      visible without DOM children changing — review b6e91x8a7 medium).

   2. FOCUS + VALUE PRESERVATION — before setting `m-klaviyo-ready`:
      a. If the Shopify fallback's email field has focus or non-empty
         value, copy the value into the Klaviyo email input (via the
         React-compatible native value setter so Klaviyo's framework
         picks up the change).
      b. If focus was on the fallback, transfer focus to Klaviyo's
         email input.
      c. Inject a transient aria-live="polite" announcement so screen
         reader users hear the swap, only when the user had been
         interacting with the fallback (silent swap on cold load).

   3. EXCLUSIVE TIMEOUT — gives Klaviyo 30 seconds to produce a usable
      form. After that, the Klaviyo mount is hidden permanently
      (display:none + aria-hidden) so a late-arriving Klaviyo form
      can't compete with the visible Shopify fallback, AND the
      MutationObserver is disconnected so post-timeout hydration
      doesn't churn through selectors + layout reads on every
      mutation. The Shopify fallback is the canonical capture from
      this point. An early-exit guard in `attemptSwap` keyed off
      `dataset.mKlaviyoTimedOut === '1'` is defense in depth in case
      any future code path re-observes the mount.

   No dependencies. ~80 lines of vanilla JS. Self-contained IIFE.
   ============================================================ */

(function () {
  'use strict';

  if (!window.MutationObserver) return;

  var SAFETY_TIMEOUT_MS = 30000;
  var MOUNT_SELECTOR = '[class^="klaviyo-form-"], [class*=" klaviyo-form-"]';

  // True only for visible, enabled, focusable form controls. The earlier
  // existence-only check (does the mount contain `input[type="email"]` +
  // a submit element?) was rejected by adversarial review — it matches
  // disabled controls, aria-hidden containers, and offscreen loader markup
  // that aren't part of a usable signup path. Hiding the working Shopify
  // fallback before Klaviyo is interactive recreates the original
  // empty-form failure mode.
  function isVisibleAndInteractive(node) {
    if (!node) return false;
    // Use the `:disabled` pseudo-class instead of just `node.disabled`.
    // The pseudo-class honors the platform's full disabled semantics —
    // it returns true for an `<input disabled>`, an `<input>` inside
    // `<fieldset disabled>`, and any other future disabled-state rules.
    // The earlier check missed the fieldset case, so a Klaviyo form
    // wrapped in a disabled fieldset (a common SaaS staging pattern)
    // would pass the gate even though no control was actually
    // submittable (review b5pmgcnxs medium).
    if (node.matches && node.matches(':disabled')) return false;
    // Walk up the ancestor chain looking for aria-hidden="true". A truthy
    // aria-hidden cuts the subtree out of the accessibility tree, so even
    // a rendered email input inside it can't be a real signup target.
    var n = node;
    while (n && n !== document.body && n.nodeType === 1) {
      if (n.getAttribute && n.getAttribute('aria-hidden') === 'true') return false;
      n = n.parentNode;
    }
    // getBoundingClientRect catches `display: none` (rect collapses to 0×0)
    // but NOT `visibility: hidden` or `opacity: 0` (rect stays non-zero
    // because layout is preserved). We use getComputedStyle to catch the
    // remaining cases (review bqpljj5tr medium). Klaviyo can stage
    // hydration with visibility:hidden + a class flip — observed in
    // multiple SaaS form-injection flows.
    var rect = node.getBoundingClientRect && node.getBoundingClientRect();
    if (!rect) return false;
    if (rect.width <= 0 || rect.height <= 0) return false;
    if (window.getComputedStyle) {
      // Walk up the ancestor chain checking every layer's computed visibility.
      // visibility / display / opacity all cascade, but each ancestor can
      // override, so we need to inspect them at each level rather than just
      // the leaf. pointer-events: none on the leaf or any ancestor blocks
      // input on the leaf (Klaviyo sometimes uses this during staged
      // hydration to lock the form before submission becomes valid).
      var a = node;
      while (a && a !== document.body && a.nodeType === 1) {
        var cs = window.getComputedStyle(a);
        if (!cs) break;
        if (cs.display === 'none') return false;
        if (cs.visibility === 'hidden' || cs.visibility === 'collapse') return false;
        if (parseFloat(cs.opacity) === 0) return false;
        if (cs.pointerEvents === 'none') return false;
        a = a.parentNode;
      }
    }
    return true;
  }

  function isUsableKlaviyoForm(mount) {
    if (!mount || !mount.querySelector) return false;
    var email = mount.querySelector('input[type="email"]');
    if (!isVisibleAndInteractive(email)) return false;
    var submit = mount.querySelector(
      'button[type="submit"], input[type="submit"], button:not([type])'
    );
    if (!isVisibleAndInteractive(submit)) return false;
    // Both controls must share a coherent submission scope: either both
    // inside the same <form>, or neither inside any form (Klaviyo's modern
    // SPA-style forms attach handlers via JS without a wrapping <form>).
    // Reject the case where one is in a form and the other isn't, which
    // would mean they're not part of the same submission flow.
    var emailForm = email.closest && email.closest('form');
    var submitForm = submit.closest && submit.closest('form');
    if (emailForm !== submitForm) return false;
    return true;
  }

  function setReactCompatibleValue(input, value) {
    var proto = window.HTMLInputElement && window.HTMLInputElement.prototype;
    var descriptor = proto && Object.getOwnPropertyDescriptor(proto, 'value');
    if (descriptor && descriptor.set) {
      descriptor.set.call(input, value);
    } else {
      input.value = value;
    }
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function migrateFocusAndValue(fallback, mount) {
    var fallbackEmail = fallback && fallback.querySelector('input[type="email"]');
    var klaviyoEmail = mount.querySelector('input[type="email"]');
    if (!fallbackEmail || !klaviyoEmail) return false;
    var fallbackHadFocus = fallback.contains(document.activeElement);
    var fallbackValue = fallbackEmail.value || '';
    var hadInteraction = fallbackHadFocus || fallbackValue.length > 0;
    if (fallbackValue) {
      setReactCompatibleValue(klaviyoEmail, fallbackValue);
    }
    if (fallbackHadFocus) {
      try { klaviyoEmail.focus(); } catch (_e) {}
    }
    return hadInteraction;
  }

  function announceSwap() {
    var live = document.createElement('span');
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    live.style.cssText =
      'position:absolute!important;left:-10000px!important;width:1px!important;' +
      'height:1px!important;overflow:hidden!important;white-space:nowrap!important;';
    live.textContent =
      'Newsletter signup form has loaded. Please continue in the visible form.';
    document.body.appendChild(live);
    setTimeout(function () { live.remove(); }, 5000);
  }

  function watchMount(mount) {
    if (mount.dataset.mKlaviyoWatched === '1') return;
    mount.dataset.mKlaviyoWatched = '1';
    var fallback = mount.nextElementSibling;
    if (!fallback || !fallback.classList.contains('m-klaviyo-fallback')) return;

    var swapped = false;
    var observer = null;
    var timeout = null;

    function attemptSwap() {
      // Short-circuit before any DOM-reading work. Two cases:
      // - Already swapped → done.
      // - Timeout fired → mount is canonical-failed, fallback has won.
      //   Skip the selector + getBoundingClientRect work that would
      //   otherwise run on every late Klaviyo mutation under a hidden
      //   subtree (adversarial review bpbpriqlo medium finding).
      if (swapped) return true;
      if (mount.dataset.mKlaviyoTimedOut === '1') return false;
      if (!isUsableKlaviyoForm(mount)) return false;
      var hadInteraction = migrateFocusAndValue(fallback, mount);
      mount.classList.add('m-klaviyo-ready');
      if (hadInteraction) announceSwap();
      swapped = true;
      if (observer) observer.disconnect();
      if (timeout) clearTimeout(timeout);
      return true;
    }

    if (attemptSwap()) return;

    observer = new MutationObserver(attemptSwap);
    // Watch BOTH child insertions/removals AND a tight set of attribute
    // mutations. Klaviyo can insert a hidden/disabled form shell first
    // (childList fires, gate fails), then make it usable by toggling
    // class / style / aria-hidden / disabled / type / hidden WITHOUT
    // adding or removing children — childList alone would never re-fire
    // and m-klaviyo-ready would never be set, leaving a now-visible
    // Klaviyo form competing with the Shopify fallback (review b6e91x8a7
    // medium). `type` is included because the readiness gate selects
    // `input[type="email"]` and `button[type="submit"]` — a placeholder
    // shell with `type="text"` flipped to `type="email"` would otherwise
    // bypass detection (review bqpljj5tr medium). The attributeFilter
    // is bounded to the attributes the gate actually reads, so we don't
    // pay for every other Klaviyo attribute change.
    observer.observe(mount, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'disabled', 'hidden', 'type']
    });

    // EXCLUSIVE TIMEOUT: after 30s without a usable Klaviyo form, commit
    // to the Shopify fallback as the canonical capture. Hide the Klaviyo
    // mount via display:none + aria-hidden so a late-arriving Klaviyo
    // form can't appear alongside the fallback (which would produce two
    // newsletter forms in the same slot — adversarial review bopqt638n).
    //
    // We also disconnect the MutationObserver here. Late Klaviyo
    // hydration under a hidden mount would otherwise keep firing
    // `attemptSwap` on every subtree mutation, running selectors and
    // getBoundingClientRect through isUsableKlaviyoForm only to reject
    // the hidden controls — wasted main-thread + layout work on the
    // degraded-dependency path (adversarial review bpbpriqlo medium).
    // The early-exit guard in attemptSwap (`mKlaviyoTimedOut === '1'`)
    // is defense in depth in case any code path later re-observes the
    // mount.
    timeout = setTimeout(function () {
      if (swapped) return;
      mount.style.display = 'none';
      mount.setAttribute('aria-hidden', 'true');
      mount.dataset.mKlaviyoTimedOut = '1';
      if (observer) observer.disconnect();
    }, SAFETY_TIMEOUT_MS);
  }

  function init() {
    var mounts = document.querySelectorAll(MOUNT_SELECTOR);
    Array.prototype.forEach.call(mounts, watchMount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   Shop the Ritual — bundle CTA "Add all to cart" handler (v2, 2026-05-11)
   ────────────────────────────────────────────────────────────
   Pairs with `sections/article-shop-the-ritual.liquid` when the
   bundle CTA action setting is `cart_add_all`. The Liquid emits the
   variant ID list on `data-sr-cta-add-ids` as a comma-separated
   string of integers (one default variant per product). This
   handler intercepts clicks, POSTs each variant to /cart/add.js
   sequentially (Shopify's recommended pattern; parallel POSTs can
   hit rate-limit on some storefronts), then redirects to /cart.

   Safe fallbacks:
   - When the data attribute is missing or empty, the link's href
     (the first product's PDP) is allowed to navigate normally —
     the same behavior as `first_product` action mode. So a
     misconfigured cart_add_all gracefully degrades to "see the
     first product" rather than breaking the click.
   - JS disabled / blocked: same fallback (default link navigation).
   - One variant fails to add (sold-out / variant rules): we surface
     a console warning and still navigate to /cart so the user can
     see what was added vs. missing.
   ============================================================ */

(function () {
  'use strict';

  var SELECTOR = '.sr-bundle-cta[data-sr-cta-action="cart_add_all"]';

  function readVariantIds(el) {
    var raw = el.getAttribute('data-sr-cta-add-ids') || '';
    var ids = raw.split(',')
      .map(function (s) { return s.trim(); })
      .filter(function (s) { return s.length > 0 && /^\d+$/.test(s); })
      .map(function (s) { return parseInt(s, 10); });
    return ids;
  }

  function addOne(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    });
  }

  /*
   * destinationUrl(code) — returns the URL the browser should navigate
   * to after all variants are added. When a `code` is supplied, we
   * route through Shopify's canonical `/discount/<code>?redirect=/cart`
   * endpoint: Shopify processes the discount (sets the session
   * mechanism that auto-applies at checkout) AND then redirects to
   * /cart in a SINGLE atomic browser navigation. No Image() beacon,
   * no fragile timing window, no race between Set-Cookie and the
   * `/cart` request. This is more reliable than the legacy
   * `Image().src = '/discount/CODE'` pattern (which had reports of the
   * cookie not being set in May 2025). A typo'd or expired code
   * results in /cart loading without the discount — clean soft-fail,
   * same outcome as no code at all.
   *
   * Returns '/cart' when no code is set.
   */
  function destinationUrl(code) {
    if (!code) return '/cart';
    var trimmed = String(code).trim();
    if (!trimmed) return '/cart';
    return '/discount/' + encodeURIComponent(trimmed) + '?redirect=' + encodeURIComponent('/cart');
  }

  function announceError(button, message) {
    // Inline status node next to the button — accessible via aria-live
    // so screen readers hear the failure. We re-use the same node on
    // repeated failures so multiple error states don't pile up in the DOM.
    var liveId = button.id ? button.id + '-status' : null;
    var live = liveId ? document.getElementById(liveId) : null;
    if (!live) {
      live = document.createElement('span');
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.className = 'sr-bundle-cta-status';
      live.style.cssText =
        'display:block;margin-top:10px;font-family:var(--font-sans);font-size:12px;color:#a14a3f;';
      if (liveId) live.id = liveId;
      button.insertAdjacentElement('afterend', live);
    }
    live.textContent = message;
  }

  function bind(button) {
    if (button.dataset.srCtaBound === '1') return;
    button.dataset.srCtaBound = '1';
    button.addEventListener('click', function (event) {
      // In-flight guard: prevent re-entrant activation. A double-click
      // or impatient repeat click before the redirect completes used to
      // run the full POST chain twice, leaving the cart with duplicate
      // quantities (review b61ybo5w7 high #1). The guard combines
      // dataset.srCtaBusy + pointer-events:none + aria-disabled so
      // mouse, keyboard, and screen-reader activation are all blocked
      // while busy.
      if (button.dataset.srCtaBusy === '1') {
        event.preventDefault();
        return;
      }
      var ids = readVariantIds(button);
      // The Liquid emits the number of products RENDERED in the bundle
      // (rendered_count) as data-sr-cta-expected, regardless of whether
      // each product had an available variant. ids reflects the subset
      // of products with an available variant. If a merchant bundles 4
      // products and 1 is sold-out, expected=4 and ids.length=3 — and
      // the difference (skipped) MUST be counted as a failure so we
      // don't redirect with a silent partial cart (review ba5mz8izg
      // high #1). When the attribute is absent (older renders or
      // non-cart_add_all CTAs), fall back to ids.length so accounting
      // still works correctly.
      var expectedRaw = button.getAttribute('data-sr-cta-expected');
      var expected = parseInt(expectedRaw || '', 10);
      if (!isFinite(expected) || expected < 0) {
        expected = ids.length;
      }
      var skipped = Math.max(0, expected - ids.length);

      if (ids.length === 0) {
        if (expected > 0) {
          // Every rendered product was skipped because none had an
          // available variant. Falling through to default link nav
          // would point the user at a sold-out product page with no
          // explanation. Announce the failure instead.
          event.preventDefault();
          announceError(button, 'Sorry — none of the bundle items are currently available. Please check back soon or contact support.');
          return;
        }
        // Nothing rendered AND nothing to add — let the link navigate
        // (graceful fallback; section was misconfigured).
        return;
      }
      event.preventDefault();
      button.dataset.srCtaBusy = '1';
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      button.style.pointerEvents = 'none';

      // Sequential add; preserves cart order + dodges rate-limit.
      // Check response.ok per request — fetch() only rejects on network
      // failures, so Shopify 4xx responses (sold out, unavailable,
      // selling-plan required, option-required) used to silently
      // "succeed" and redirect with items missing from the cart
      // (review b61ybo5w7 high #2). Track failed IDs so we can surface
      // a clear partial-success message instead of unconditional
      // redirect.
      var failedIds = [];
      var chain = Promise.resolve();
      ids.forEach(function (id) {
        chain = chain.then(function () {
          return addOne(id).then(function (response) {
            if (!response.ok) {
              failedIds.push(id);
              return response.json().then(function (data) {
                console.warn('[shop-the-ritual] /cart/add.js refused variant', id, response.status, data);
              }, function () {
                console.warn('[shop-the-ritual] /cart/add.js refused variant', id, response.status);
              });
            }
          }, function (err) {
            failedIds.push(id);
            console.warn('[shop-the-ritual] network error adding variant', id, err);
          });
        });
      });
      chain.finally(function () {
        // total_failed combines runtime POST failures with products
        // that were skipped at render time because they had no
        // available variant. Both flavors of failure must surface the
        // same way; the user doesn't care WHY an item is missing, only
        // that fewer items landed in the cart than the page advertised.
        var totalFailed = failedIds.length + skipped;
        // Read the (optional) bundle discount code AFTER the cart-add
        // chain completes. We route the redirect through Shopify's
        // /discount/<code>?redirect=/cart endpoint when a code is set —
        // single atomic navigation, no Image() beacon race. Shopify's
        // percentage discounts auto-apply to whichever bundle SKUs
        // landed in the cart (per the discount's product-restriction),
        // so partial-success carts (2 of 3 items, etc.) still receive
        // the discount on the items that DID make it in.
        var discountCode = button.getAttribute('data-sr-cta-discount') || '';
        var redirectUrl = destinationUrl(discountCode);
        if (totalFailed === 0) {
          window.location.href = redirectUrl;
          return;
        }
        if (totalFailed >= expected) {
          // Nothing made it to the cart. Restore the button so the
          // user can retry (network blip), and tell them why.
          // No /discount redirect — empty cart can't carry a discount,
          // and we want the user to see the inline error, not navigate
          // to an empty cart with a discount cookie set.
          announceError(button, 'Sorry — none of the bundle items could be added. They may be sold out. Please try again or contact support.');
          button.dataset.srCtaBusy = '0';
          button.removeAttribute('aria-busy');
          button.removeAttribute('aria-disabled');
          button.style.pointerEvents = '';
          return;
        }
        // Partial success → tell the user, give them a moment to read,
        // then redirect so they see what made it to the cart.
        var addedCount = ids.length - failedIds.length;
        announceError(button, 'Added ' + addedCount + ' of ' + expected + ' items to your cart. Some items were unavailable. Redirecting…');
        setTimeout(function () {
          window.location.href = redirectUrl;
        }, 2200);
      });
    });
  }

  function init() {
    var buttons = document.querySelectorAll(SELECTOR);
    Array.prototype.forEach.call(buttons, bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   Shop-the-Ritual — PER-CARD ADD-TO-CART handler (v3.1)
   ────────────────────────────────────────────────────────────
   Pairs with `snippets/m-shop-ritual-card.liquid`. Each ritual
   card renders a `.sr-card-add` ghost button at the foot with
   `data-sr-card-add-id` holding the first-available variant ID
   for that product. Click → POST single variant to /cart/add.js
   → redirect to /cart. No discount routing (per-card adds are
   for users buying ONE item, not the bundle — bundle discount
   only fires for the full Add-the-ritual flow).

   Why a separate handler from cart_add_all:
   - Only one variant (no sequential chain, no partial-success
     accounting, no expected-vs-actual math).
   - No discount-code routing (always `/cart`, never `/discount`).
   - Different selector + dataset attribute names so the two
     don't accidentally cross-bind.

   Replaces the third-party satcb_quick_buy widget that the v3.1
   CSS now hides inside this section.
   ============================================================ */
(function () {
  'use strict';

  var SELECTOR = '.sr-card-add[data-sr-card-add-id]';

  function announce(button, message) {
    var liveId = (button.id || 'sr-card-add') + '-status';
    var live = document.getElementById(liveId);
    if (!live) {
      live = document.createElement('span');
      live.id = liveId;
      live.className = 'sr-card-add-status';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.style.cssText = 'display:block;font-family:var(--font-sans);font-size:11px;margin-top:6px;color:#a14a3f;letter-spacing:0;text-transform:none;line-height:1.4;';
      button.insertAdjacentElement('afterend', live);
    }
    live.textContent = message;
  }

  function bind(button) {
    if (button.dataset.srCardBound === '1') return;
    button.dataset.srCardBound = '1';
    button.addEventListener('click', function (event) {
      event.preventDefault();
      // In-flight guard against double-clicks adding 2 of the same item.
      if (button.getAttribute('aria-busy') === 'true') return;
      var raw = button.getAttribute('data-sr-card-add-id') || '';
      var id = parseInt(String(raw).trim(), 10);
      if (!isFinite(id) || id <= 0) {
        announce(button, 'Sorry — this item is unavailable. Please try again or contact support.');
        return;
      }
      button.setAttribute('aria-busy', 'true');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: id, quantity: 1 })
      }).then(function (response) {
        if (!response.ok) {
          response.json().then(function (data) {
            console.warn('[shop-the-ritual] per-card add refused variant', id, response.status, data);
          }, function () {
            console.warn('[shop-the-ritual] per-card add refused variant', id, response.status);
          });
          announce(button, 'Sorry — this item could not be added. It may be sold out.');
          button.removeAttribute('aria-busy');
          return;
        }
        // Clear busy state BEFORE navigation. If the user is already on
        // /cart (same-URL nav) or a theme AJAX-cart interceptor blocks
        // the unload, the button would stay locked at aria-busy=true
        // forever. Mirrors the error paths' restore pattern.
        button.removeAttribute('aria-busy');
        window.location.href = '/cart';
      }, function (err) {
        console.warn('[shop-the-ritual] per-card network error variant', id, err);
        announce(button, 'Connection error — please try again.');
        button.removeAttribute('aria-busy');
      });
    });
  }

  function init() {
    var buttons = document.querySelectorAll(SELECTOR);
    Array.prototype.forEach.call(buttons, bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
